import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface HistoryData {
  timestamp: string;
  quote: number;
}

interface TopTradersData {
  contract_address: string;
  result: Array<{
    address: string;
    avg_buy_price_usd: string;
    avg_sell_price_usd: string;
    count_of_trades: number;
    realized_profit_percentage: number;
    realized_profit_usd: string;
    total_usd_invested: string;
  }>;
}

interface AssetHolderData {
  chain: string;
  classified_holder_counts: {
    'Contract Address': number;
    'Individual Holder': number;
  };
  raw_holders: {
    balance: Record<string, string>;
    balance_formatted: Record<string, string>;
    holder_group: Record<string, string>;
    is_contract: Record<string, boolean>;
    usd_value: Record<string, string>;
    percentage_relative_to_total_supply: Record<string, number>;
  };
}

interface PnLData {
  avg_buy_price_usd: string;
  avg_cost_of_quantity_sold: string;
  avg_sell_price_usd: string;
  chain: string;
  count_of_trades: number;
  decimals: string;
  logo: string;
  name: string;
  possible_spam: boolean;
  realized_profit_percentage: number;
  realized_profit_usd: string;
  symbol: string;
  token_address: string;
  total_buys: number;
  total_sells: number;
  total_sold_usd: string;
  total_tokens_bought: string;
  total_tokens_sold: string;
  total_usd_invested: string;
  wallet_address: string;
}

interface PairData {
  buyVolume: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  buyers: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  buys: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  currentNativePrice: number;
  currentUsdPrice: number;
  exchange: string | null;
  exchangeAddress: string;
  exchangeLogo: string | null;
  exchangeUrl: string | null;
  liquidityPercentChange: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  pairAddress: string;
  pairCreated: string;
  pairLabel: string;
  pricePercentChange: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  sellVolume: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  sellers: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  sells: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
  tokenAddress: string;
  tokenLogo: string;
  tokenName: string;
  tokenSymbol: string;
  totalLiquidityUsd: number;
  totalVolume: {
    '1h': number;
    '24h': number;
    '4h': number;
    '5min': number;
  };
}

interface LoadingState {
  returnHisogram?: boolean;
  historicHolder?: boolean;
  assetCorrelations?: boolean;
  pnl?: boolean;
  highlights?: boolean;
  history?: boolean;
  holdings?: boolean;
  assetHolder?: boolean;
  topTraders?: boolean;
  transactions?: boolean;
  bullBearMean?: boolean;
  portBench?: boolean;
  maxDrawDown?: boolean;
  assetsPeriodReturn?: boolean;
  pairData?: boolean;
}

interface PortfolioState {
  highlights: any;
  history: HistoryData[];
  loading: LoadingState;
  holdings: any[];
  transactions: any[];
  assetHolder: Record<string, AssetHolderData>;
  topTraders: Record<string, TopTradersData>;
  stockReturns: any[];
  assetCorrelations: any[];
  historicHolder: any[];
  bullBearMean: any[];
  portBench: any[];
  maxDrawDown: any[];
  assetsPeriodReturn: any;
  pnl: PnLData[];
  pairData: PairData[];
  portfolioScore: number | null;
  diversityScore: number | null;
  marketScore: number | null;
}

const initialState: PortfolioState = {
  highlights: {},
  history: [],
  loading: {
    returnHisogram: true,
    historicHolder: true,
    assetCorrelations: true,
    pnl: false,
    holdings: true,
    assetsPeriodReturn: true,
    highlights: true,
    history: true,
    assetHolder: true,
    topTraders: true,
    transactions: true,
    bullBearMean: true,
    portBench: true,
    maxDrawDown: true,
    pairData: true,
  },
  holdings: [],
  transactions: [],
  assetHolder: {},
  topTraders: {},
  stockReturns: [],
  assetCorrelations: [],
  historicHolder: [],
  bullBearMean: [],
  portBench: [],
  maxDrawDown: [],
  assetsPeriodReturn: {},
  pnl: [],
  pairData: [],
  portfolioScore: null,
  diversityScore: null,
  marketScore: null,
}

export const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setHighlights: (state, action) => {
      state.highlights = action.payload;
      state.loading.highlights = false;
    },
    setHistory: (state, action) => {
      state.history = action.payload;
      state.loading.history = false;
    },
    setHoldings: (state, action) => {
      state.holdings = action.payload;
      state.loading.holdings = false;
    },
    // setAssetHolder: (state, action: PayloadAction<AssetHolderData>) => {
    //   // Only store the serializable data
    //   const { chain, classified_holder_counts, raw_holders } = action.payload;
    //   state.assetHolder = {
    //     ...state.assetHolder,
    //     [action.payload.chain]: {
    //       chain,
    //       classified_holder_counts,
    //       raw_holders
    //     }
    //   };
    //   state.loading.assetHolder = false;
    // },
    setAssetHolder: (state, action) => {
      // Store the whole response object directly
      state.assetHolder = {
        ...state.assetHolder,
        ...action.payload
      };
      state.loading.assetHolder = false;
    },
    setTopTraders: (state, action: PayloadAction<any>) => {
      // Extract only the necessary data from the API response
      const { data } = action.payload;
      if (data && typeof data === 'object') {
        // Ensure we're only storing serializable data
        const serializedData = Object.entries(data).reduce((acc: any, [key, value]: [string, any]) => {
          if (value && typeof value === 'object') {
            acc[key] = {
              contract_address: value.contract_address,
              result: value.result
            };
          }
          return acc;
        }, {});
        state.topTraders = serializedData;
      }
      state.loading.topTraders = false;
    },
    setTransactions: (state, action) => {
      state.transactions = action.payload;
      state.loading.transactions = false;
    },
    setStockReturns: (state, action) => {
      state.stockReturns = action.payload;
      state.loading.returnHisogram = false;
    },
    setAssetCorrelations: (state, action) => {
      state.assetCorrelations = action.payload;
      if (action.payload && typeof action.payload.score === 'number') {
        state.diversityScore = action.payload.score;
      }
      state.loading.assetCorrelations = false;
    },
    setHistoricHolder: (state, action) => {
      state.historicHolder = action.payload;
      state.loading.historicHolder = false;
    },
    setBullBearMean: (state, action) => {
      state.bullBearMean = action.payload;
      state.loading.bullBearMean = false;
    },
    setPortBench: (state, action) => {
      state.portBench = action.payload;
      if (action.payload && typeof action.payload.score === 'number') {
        state.marketScore = action.payload.score;
      }
      state.loading.portBench = false;
    },
    setPairData: (state, action: PayloadAction<PairData[]>) => {
      // Always set the pair data, even if it's an empty array
      // This ensures stale data is cleared when an empty response is received
      state.pairData = action.payload;
      state.loading.pairData = false;
    },
    isLoading: (state, action: PayloadAction<Partial<LoadingState>>) => {
      (Object.keys(action.payload) as Array<keyof LoadingState>).forEach(key => {
        if (key in state.loading) {
          state.loading[key] = action.payload[key] ?? false;
        }
      });
    },
    resetPortfolioData: (state) => {
      return {
        ...initialState,
        loading: {
          ...initialState.loading
        }
      };
    },
    setMaxDrawDown: (state, action) => {
      state.maxDrawDown = action.payload;
      if (action.payload?.score !== undefined) {
        state.portfolioScore = Number(action.payload.score);
      }
      state.loading.maxDrawDown = false;
    },
    setPortfolioScore: (state, action: PayloadAction<number>) => {
      const score = Number(action.payload);
      if (!isNaN(score)) {
        state.portfolioScore = score;
      }
    },
    setDiversityScore: (state, action: PayloadAction<number>) => {
      state.diversityScore = action.payload;
    },
    setMarketScore: (state, action: PayloadAction<number>) => {
      state.marketScore = action.payload;
    },
    setAssetsPeriodReturn: (state, action) => {
      state.assetsPeriodReturn = action.payload;
      state.loading.assetsPeriodReturn = false;
    },
    setPnLData: (state, action: PayloadAction<PnLData[]>) => {
      state.pnl = Array.isArray(action.payload) ? action.payload : [];
      state.loading.pnl = false;
    },
    resetHoldings: (state) => {
      state.holdings = [];
      state.loading.holdings = true;
    },
    setWalletChanged: (state) => {
      state.loading.holdings = true;
      state.loading.pnl = true;
      state.loading.highlights = true;
      state.loading.pairData = true;
    },
  },
});

export const {
  setHighlights,
  setHistory,
  setHoldings,
  setTransactions,
  isLoading,
  resetPortfolioData,
  setAssetHolder,
  setTopTraders,
  setStockReturns,
  setAssetCorrelations,
  setHistoricHolder,
  setBullBearMean,
  setPortBench,
  setMaxDrawDown,
  setAssetsPeriodReturn,
  setPnLData,
  setPairData,
  resetHoldings,
  setWalletChanged,
  setPortfolioScore,
  setDiversityScore,
  setMarketScore
} = portfolioSlice.actions;

export default portfolioSlice.reducer;